import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Tooltip, IconButton } from "@mui/material";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import DeleteIcon from "@mui/icons-material/Delete";

import styles from "./UserManagement.module.css";
import CustomMRTTable from "../../components/CustomMRTTable/CustomMRTTable";
import { AxiosInstance } from "../../services/Api";
import PopupModal from "../../components/PopupModal/PopupModal";

const UserManagement = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null, // "delete" | "resend"
    row: null,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.get(
        `/licenses/user?page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`,
      );

      if (response?.data?.success) {
        setUsers(response?.data?.data?.licenses || []);
        const apiPagination = response?.data?.data?.pagination;
        setTotalRecords(apiPagination?.total || 0);
      } else {
        setUsers([]);
        setTotalRecords(0);
        throw new Error(response?.data?.message || "Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
      setUsers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteUser = useCallback((row) => {
    if (!row?.id) return;
    setConfirmState({ open: true, action: "delete", row });
  }, []);

  const confirmDeleteUser = useCallback(
    async (row) => {
      setDeletingId(row.id);

      try {
        const response = await AxiosInstance.delete(`/licenses/${row.id}`);

        if (response?.status === 200 && response?.data?.success) {
          setModalState({
            open: true,
            type: "success",
            message: response?.data?.message || "User deleted successfully!",
            redirectPath: "",
          });
          fetchUsers();
        } else {
          throw new Error(response?.data?.message || "Failed to delete user");
        }
      } catch (err) {
        console.error("Error deleting user:", err);
        setModalState({
          open: true,
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to delete user",
          redirectPath: "",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [fetchUsers],
  );

  const handleValidateUser = useCallback((row) => {
    if (!row?.id) return;
    setConfirmState({ open: true, action: "resend", row });
  }, []);

  const confirmValidateUser = useCallback(
    async (row) => {
      try {
        const response = await AxiosInstance.post(
          `/licenses/${row.id}/resend-activation`,
        );

        if (response?.status === 200 && response?.data?.success) {
          setModalState({
            open: true,
            type: "success",
            message:
              response?.data?.message || "Activation email sent successfully!",
            redirectPath: "",
          });
          fetchUsers();
        } else {
          throw new Error(response?.data?.message || "Failed to validate user");
        }
      } catch (err) {
        console.error("Error validating user:", err);
        setModalState({
          open: true,
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to validate user",
          redirectPath: "",
        });
      }
    },
    [fetchUsers],
  );

  const handleConfirmAction = useCallback(() => {
    const { action, row } = confirmState;
    if (!row?.id) return;

    setConfirmState({ open: false, action: null, row: null });

    if (action === "delete") {
      confirmDeleteUser(row);
    } else if (action === "resend") {
      confirmValidateUser(row);
    }
  }, [confirmState, confirmDeleteUser, confirmValidateUser]);

  const handleExcelDownload = useCallback(async () => {
    try {
      if (!users.length) return;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "User Management";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Users");
      worksheet.columns = [
        { header: "S.No", key: "sno", width: 8 },
        { header: "Role Name", key: "role_display_name", width: 22 },
        { header: "User Name", key: "name", width: 22 },
        { header: "User Email", key: "email", width: 32 },
        { header: "School Name", key: "school_name", width: 28 },
        { header: "Is Validated", key: "is_validated", width: 14 },
      ];

      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, size: 11 };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0E0" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF999999" } },
          left: { style: "thin", color: { argb: "FF999999" } },
          bottom: { style: "thin", color: { argb: "FF999999" } },
          right: { style: "thin", color: { argb: "FF999999" } },
        };
      });

      users.forEach((item, index) => {
        worksheet.addRow({
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          role_display_name: item.role_display_name || "",
          name: item.name || "",
          email: item.email || "",
          school_name: item.school_name || "",
          is_validated: Number(item.is_validated) === 1 ? "Yes" : "No",
        });
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: colNumber === 1 ? "center" : "left",
            wrapText: true,
          };
          cell.font = { size: 10 };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        blob,
        `USER_MANAGEMENT_Page_${pagination.pageIndex + 1}.xlsx`,
      );
    } catch (err) {
      console.error("Error generating Excel:", err);
    }
  }, [users, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "role_display_name",
        header: "Role Name",
        size: 180,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "name",
        header: "User Name",
        size: 180,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "email",
        header: "User Email",
        size: 240,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "school_name",
        header: "School Name",
        size: 200,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "is_validated",
        header: "Is Validated",
        size: 130,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: ["Yes", "No"],
        Cell: ({ cell }) => {
          const isValidated = Number(cell.getValue()) === 1;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: isValidated ? "#E8F5E9" : "#FFEBEE",
                color: isValidated ? "#2E7D32" : "#C62828",
                border: `1px solid ${isValidated ? "#A5D6A7" : "#EF9A9A"}`,
              }}
            >
              {isValidated ? "Yes" : "No"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => {
          const isValidated = Number(row.original.is_validated) === 1;
          const isDeleting = deletingId === row.original.id;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Tooltip title="Resend activation email" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleValidateUser(row.original);
                    }}
                    sx={{
                      color: isValidated ? "#2E7D32" : "#C62828",
                      backgroundColor: isValidated ? "#E8F5E9" : "#FFEBEE",
                      "&:hover": {
                        backgroundColor: isValidated ? "#C8E6C9" : "#FFCDD2",
                      },
                    }}
                  >
                    <GpsFixedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete user" arrow>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isDeleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteUser(row.original);
                    }}
                  >
                    {isDeleting ? (
                      <i className="fa-solid fa-spinner fa-spin" />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [
      pagination.pageIndex,
      pagination.pageSize,
      deletingId,
      handleDeleteUser,
      handleValidateUser,
    ],
  );

  const handleModalOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleModalOk}
      />

      <PopupModal
        open={confirmState.open}
        title="Confirm"
        render
        onClose={() => setConfirmState({ open: false, action: null, row: null })}
        onOk={handleConfirmAction}
        renderData={() => (
          <div>
            <p
              style={{
                margin: "0 0 20px",
                textAlign: "center",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {confirmState.action === "delete" ? (
                <>
                  Are you sure you want to delete{" "}
                  <strong>
                    {confirmState.row?.email || confirmState.row?.name}
                  </strong>
                  ?
                </>
              ) : (
                <>
                  Are you sure you want to resend activation email to{" "}
                  <strong>
                    {confirmState.row?.email || confirmState.row?.name}
                  </strong>
                  ?
                </>
              )}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setConfirmState({ open: false, action: null, row: null })
                }
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  border: "none",
                  background:
                    confirmState.action === "delete" ? "#C62828" : "#1565C0",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {confirmState.action === "delete" ? "Delete" : "Resend"}
              </button>
            </div>
          </div>
        )}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>USER MANAGEMENT</h1>
          <div
            className={styles.headerActions}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "nowrap",
            }}
          >
            <Tooltip title="Refresh list" arrow>
              <IconButton
                size="medium"
                onClick={fetchUsers}
                className={styles.menuButton}
                aria-label="Refresh"
                disabled={loading}
              >
                <i className="fa-solid fa-rotate" style={{ fontSize: "18px", color: "#333" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download Excel (current page)" arrow>
              <IconButton
                size="medium"
                onClick={handleExcelDownload}
                className={styles.menuButton}
                aria-label="Download Excel"
                disabled={loading || users.length === 0}
              >
                <i className="fa-solid fa-download" style={{ fontSize: "18px", color: "#333" }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {error ? (
          <div className={styles.errorMessage}>
            {error}
            <button type="button" onClick={fetchUsers} className={styles.retryButton}>
              Retry
            </button>
          </div>
        ) : null}

        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={users}
            loading={loading}
            manualPagination
            manualFiltering={false}
            rowCount={totalRecords}
            pagination={pagination}
            onPaginationChange={setPagination}
            enablePagination
            enableSorting
            enableColumnFilters
            enableSearch
            enableColumnVisibility
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableStickyHeader
            placeholder="Search users on this page..."
          />
        </div>
      </div>
    </>
  );
};

export default UserManagement;